import React, { useEffect, useState } from "react";
import { Search, ShieldCheck, MoreVertical, Pencil, Power, Star, Flag, TrendingDown, Banknote, Landmark, Ban, Trash2, Users, CheckCircle2, AlertCircle, Bell, Settings } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { inr, catLabel, apiError } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AdminEditModal, SetCreditModal, AdminWithdrawModal, OldFundModal, OldLoanModal,
} from "@/components/AdminModals";
import MemberDetailModal from "@/components/MemberDetailModal";

const filters = [
  { key: "all", hi: "सभी", en: "All", icon: Users, active: "bg-primary text-white border-primary", idle: "bg-blue-50 text-primary border-blue-100" },
  { key: "regular", hi: "नियमित", en: "Regular", icon: CheckCircle2, active: "bg-emerald-500 text-white border-emerald-500", idle: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  { key: "defaulter", hi: "डिफॉल्टर", en: "Defaulter", icon: AlertCircle, active: "bg-red-500 text-white border-red-500", idle: "bg-red-50 text-red-700 border-red-100" },
];

export default function MembersScreen() {
  const { member } = useAuth();
  const isAdmin = member.role === "admin";
  const [members, setMembers] = useState([]);
  const [group, setGroup] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ type: null, target: null });
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    const [m, g] = await Promise.all([api.get("/members"), api.get("/group")]);
    setMembers(m.data);
    setGroup(g.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const regCount = members.filter((m) => m.category === "regular").length;
  const defCount = members.filter((m) => m.category === "defaulter").length;
  const counts = { all: members.length, regular: regCount, defaulter: defCount };

  const shown = members
    .filter((m) => filter === "all" || m.category === filter)
    .filter((m) => (m.name + m.name_hi).toLowerCase().includes(q.toLowerCase()));

  const openModal = (type, target) => setModal({ type, target });
  const closeModal = (v) => !v && setModal({ type: null, target: null });

  return (
    <div className="space-y-4">
      {/* Group header */}
      {group && (
        <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-display font-bold text-xl text-blue-900">{group.name_en}</p>
              <p className="text-[11px] text-blue-700 flex items-center gap-1">SHG • Self Help Group • {group.name_hi} <ShieldCheck className="h-3 w-3 text-emerald-600" /></p>
            </div>
            <div className="flex gap-1.5">
              <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center"><Bell className="h-4 w-4 text-blue-700" /></div>
              <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center"><Settings className="h-4 w-4 text-blue-700" /></div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="font-display font-extrabold text-2xl text-blue-900">सदस्य / Members</p>
            <span className="font-display font-bold text-xl text-blue-900">({group.total_members})</span>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="grid grid-cols-3 gap-2">
        {filters.map((f) => {
          const A = filter === f.key;
          const Icon = f.icon;
          return (
            <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)} className={`py-2.5 rounded-xl border text-center transition-colors ${A ? f.active : f.idle}`}>
              <Icon className="h-4 w-4 mx-auto mb-0.5" />
              <p className="font-display font-semibold text-sm">{f.hi}</p>
              <p className="text-[11px] opacity-90">{f.en} ({counts[f.key]})</p>
            </button>
          );
        })}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 flex items-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          <div><p className="font-display font-bold text-emerald-700">नियमित</p><p className="text-xs text-emerald-600">Regular: {regCount} सदस्य</p></div>
        </div>
        <div className="rounded-2xl bg-red-50 border border-red-100 p-3 flex items-center gap-2">
          <AlertCircle className="h-6 w-6 text-red-600" />
          <div><p className="font-display font-bold text-red-700">डिफॉल्टर</p><p className="text-xs text-red-600">Defaulter: {defCount} सदस्य</p></div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input data-testid="member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="सदस्य खोजें / Search members…" className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-primary" />
      </div>

      {/* Member cards */}
      <div className="space-y-2.5">
        {shown.map((m) => {
          const cat = catLabel(m.category);
          const hasLoan = m.loan_remaining > 0;
          return (
            <div key={m.id} data-testid={`member-card-${m.id}`} className="rounded-2xl bg-white p-3.5 shadow-sm border border-slate-100 flex items-center gap-3">
              <Avatar name={m.name} size={46} />
              <div className="flex-1 min-w-0 cursor-pointer" data-testid={`member-open-${m.id}`} onClick={() => setDetailId(m.id)}>
                <div className="flex items-center gap-1.5">
                  <p className="font-semibold text-sm truncate">{m.name} • {m.name_hi}</p>
                  {m.is_blacklisted && <Ban className="h-3.5 w-3.5 text-red-500" />}
                </div>
                <p className="text-xs text-muted-foreground">योगदान / Contribution: {inr(m.contribution)}</p>
                <span className="inline-flex items-center gap-1 mt-0.5 text-[11px]">
                  <span className={`h-1.5 w-1.5 rounded-full ${cat.dot}`} /> {cat.hi} / {cat.en}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <span className={`px-2.5 py-1 rounded-lg border text-[11px] font-mono ${hasLoan ? "border-red-200 text-red-600" : "border-blue-200 text-primary"}`} data-testid={`member-loan-${m.id}`}>
                  ऋण / Loan: {hasLoan ? inr(m.loan_remaining) : "—"}
                </span>
                {isAdmin && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`admin-actions-${m.id}`} className="h-7 w-7 rounded-lg hover:bg-slate-100 flex items-center justify-center">
                        <MoreVertical className="h-4 w-4 text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem data-testid={`action-edit-${m.id}`} onClick={() => openModal("edit", m)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-toggle-status-${m.id}`} onClick={() => act(() => api.post(`/admin/members/${m.id}/toggle-status`), "Status badla")}><Power className="h-4 w-4 mr-2" /> {m.status === "active" ? "Inactive" : "Active"} करें</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-set-credit-${m.id}`} onClick={() => openModal("credit", m)}><Star className="h-4 w-4 mr-2" /> Set Credit</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-npa-${m.id}`} onClick={() => act(() => api.post(`/admin/members/${m.id}/toggle-npa`), "NPA toggle")}><Flag className="h-4 w-4 mr-2" /> {m.is_npa ? "Unmark" : "Mark"} NPA</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-admin-withdraw-${m.id}`} onClick={() => openModal("withdraw", m)}><TrendingDown className="h-4 w-4 mr-2" /> Admin Withdraw</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`action-old-fund-${m.id}`} onClick={() => openModal("oldfund", m)}><Banknote className="h-4 w-4 mr-2" /> Old Fund</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-old-loan-${m.id}`} onClick={() => openModal("oldloan", m)}><Landmark className="h-4 w-4 mr-2" /> Old Loan</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`action-blacklist-${m.id}`} onClick={() => act(() => api.post(`/admin/members/${m.id}/blacklist`), "Blacklist toggle")}><Ban className="h-4 w-4 mr-2" /> {m.is_blacklisted ? "Un-blacklist" : "Blacklist"}</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`action-remove-${m.id}`} className="text-destructive" onClick={() => act(() => api.post(`/admin/members/${m.id}/remove`), "Member hata diya")}><Trash2 className="h-4 w-4 mr-2" /> Remove</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          );
        })}
        {shown.length === 0 && <p className="text-center text-muted-foreground py-6 text-sm">कोई सदस्य नहीं मिला</p>}
      </div>

      {/* Footer count */}
      {shown.length > 0 && (
        <div className="text-center text-xs text-muted-foreground">
          <p className="font-display font-semibold">दिखा रहे {shown.length} में से {members.length} सदस्य • सूची स्क्रॉल करें</p>
          <p>Showing {shown.length} of {members.length} members</p>
        </div>
      )}

      <AdminEditModal open={modal.type === "edit"} onOpenChange={closeModal} target={modal.target} onDone={load} />
      <SetCreditModal open={modal.type === "credit"} onOpenChange={closeModal} target={modal.target} onDone={load} />
      <AdminWithdrawModal open={modal.type === "withdraw"} onOpenChange={closeModal} target={modal.target} onDone={load} />
      <OldFundModal open={modal.type === "oldfund"} onOpenChange={closeModal} target={modal.target} onDone={load} />
      <OldLoanModal open={modal.type === "oldloan"} onOpenChange={closeModal} target={modal.target} onDone={load} />
      <MemberDetailModal memberId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} />
    </div>
  );
}
